import React from "react";
import { M } from "@/lib/motionStatic";
import { ArrowRight, PlayCircle, Sparkles, CheckCircle2, CreditCard, Hourglass } from "lucide-react";
import { ASSETS } from "@/data/content";
import { SIMPLE_RATE } from "@/data/pricing";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MilestoneCard = ({ delay = 0, icon, title, sub, subColor, testid }) => (
  <M.div
    data-testid={testid}
    initial={{ opacity: 0, scale: 0.9, y: 12 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    className="relative z-10 glass-float rounded-2xl px-4 py-3 flex items-center gap-3 w-[220px] sm:w-[256px]"
  >
    <div className="shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-sm font-bold text-[#091630] leading-tight">{title}</p>
      {sub && (
        <p className="text-[11px] font-bold leading-tight mt-0.5" style={{ color: subColor }}>
          {sub}
        </p>
      )}
    </div>
  </M.div>
);

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-32 pb-16 lg:pt-36">
      {/* soft background accents */}
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#ecfdf3] blur-3xl opacity-70" />
      <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-[#ecfeff] blur-3xl opacity-70" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <M.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#091630] px-4 py-1.5 text-xs font-bold text-[#21c55e]">
            <Sparkles className="h-3.5 w-3.5" />
            {SIMPLE_RATE} on domestic cards · powered by Stripe
          </span>
        </M.div>

        <M.h1
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={1}
          className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-[#091630] leading-[1.02]"
        >
          Stop chasing <span className="text-gradient">payments.</span>
        </M.h1>

        <M.p
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={2}
          className="mt-6 text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed"
        >
          Workpay helps vendors collect project payments, track milestones, and get paid with clarity — while
          customers stay protected.
        </M.p>

        <M.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={3}
          className="mt-8 flex flex-col sm:flex-row gap-3 justify-center"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollTo("#early-access")}
            className="bg-[#21c55e] text-[#091630] font-bold py-3.5 px-7 rounded-full hover:bg-[#16a34a] hover:text-white transition-colors flex items-center justify-center gap-2 green-shadow"
          >
            Join Early Access <ArrowRight className="h-5 w-5" />
          </button>
          <button
            data-testid="hero-secondary-button"
            onClick={() => scrollTo("#how-it-works")}
            className="bg-white text-[#091630] font-semibold py-3.5 px-7 rounded-full border border-slate-200 hover:border-slate-300 transition-colors flex items-center justify-center gap-2 soft-shadow"
          >
            <PlayCircle className="h-5 w-5 text-[#16a34a]" /> See how it works
          </button>
        </M.div>

        <M.p
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={4}
          className="mt-6 text-sm text-slate-400"
        >
          Project workflow by Workpay. Payments powered by Stripe.
        </M.p>
      </div>

      {/* Visual band: vendor + customer composite with centered milestone column */}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
        <M.div
          data-testid="hero-visual"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[2.5rem] overflow-hidden border border-slate-100 soft-shadow-lg bg-[#f4fbf6]"
        >
          {/* Mobile: vertical composite */}
          <img
            src={ASSETS.heroMobile}
            alt="Workpay vendor and customer"
            fetchPriority="high"
            decoding="async"
            className="block sm:hidden w-full h-auto"
          />
          {/* Desktop / tablet: horizontal composite */}
          <img
            src={ASSETS.heroComposite}
            alt="Workpay vendor and customer"
            fetchPriority="high"
            decoding="async"
            className="hidden sm:block w-full h-auto"
          />

          {/* centered vertical milestone column */}
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="relative flex flex-col items-center gap-3 sm:gap-4">
              {/* dashed connector */}
              <div className="absolute top-7 bottom-7 left-1/2 -translate-x-1/2 border-l-2 border-dashed border-[#21c55e]/60" />

              <MilestoneCard
                testid="hero-payment-card"
                delay={0.9}
                title="AED 2,450"
                sub="Paid via Stripe"
                subColor="#16a34a"
                icon={
                  <div className="h-9 w-9 rounded-full bg-[#ecfdf3] flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-[#16a34a]" />
                  </div>
                }
              />

              <MilestoneCard
                testid="hero-milestone-chip"
                delay={1.05}
                title="Milestone approved"
                icon={
                  <div className="h-9 w-9 rounded-full bg-[#ecfdf3] flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-[#16a34a]" />
                  </div>
                }
              />

              <MilestoneCard
                testid="hero-progress-card"
                delay={1.2}
                title="Project 75% complete"
                icon={
                  <div
                    className="relative h-9 w-9 rounded-full"
                    style={{ background: "conic-gradient(#7c3aed 75%, #e9d5ff 0)" }}
                  >
                    <div className="absolute inset-[3px] rounded-full bg-white flex items-center justify-center">
                      <span className="text-[9px] font-extrabold text-[#091630]">75%</span>
                    </div>
                  </div>
                }
              />

              <MilestoneCard
                testid="hero-approval-card"
                delay={1.35}
                title="Customer approval"
                sub="Pending"
                subColor="#d97706"
                icon={
                  <div className="h-9 w-9 rounded-full bg-[#fff7ed] flex items-center justify-center">
                    <Hourglass className="h-4 w-4 text-[#d97706]" />
                  </div>
                }
              />

              <M.div
                data-testid="hero-stripe-card"
                initial={{ opacity: 0, scale: 0.9, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 glass-float rounded-2xl px-5 py-2.5 flex items-center gap-2 w-[220px] sm:w-[256px] justify-center"
              >
                <span className="text-xs font-semibold text-slate-500">Powered by</span>
                <span className="font-display text-base font-extrabold text-[#635BFF]">stripe</span>
              </M.div>
            </div>
          </div>
        </M.div>
      </div>
    </section>
  );
};

export default Hero;
